import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import ProcessDataTable from 'components/core/ProcessDataTable';
import RIEInput from 'components/core/editable/RIEInput';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import NewRetrievalRuleVariable from 'components/core/modals/NewRetrievalRuleVariable';
import ComponentUtils from 'utils/ComponentUtils';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ProcessValidator from 'utils/ProcessValidator';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import ModalsService from 'services/ModalsService';
import DodService from 'services/DodService';
import _ from 'lodash';

/**
 * Renders the variable mappings for a given input dataset
 */
export default class MappingsTable extends ProcessDataTable {
    static propTypes = {
        inputDatasetName: PropTypes.string,
        highlightVariableName: PropTypes.string, // specific variable name you want to highlight in the table
        hideToolbar: PropTypes.bool,
        displayOnlyVariable: PropTypes.string // only display specific variable
    };

    componentDidMount() {
        // Make sure our datastream variables are loaded
        const rules = this.inputDataset.rules;
        rules.forEach(rule => {
            // We have to fetch datastream outside of the render method or it throws an error
            DodService.fetchDodVarsIfNeeded(rule.datastream_name);
        });
    }

    _initializePropDependentVariables(props) {
        super._initializePropDependentVariables(props);

        this.inputDataset = props.process.variable_retrieval.input_datasets[props.inputDatasetName];
        this.priorityOptions = [];
        const errorPaths = {};

        if (this.inputDataset.rules) {
            const rules = this.inputDataset.rules;
            rules.forEach((rule) => {
                const label = `${rule.datastream_name} (priority ${rule.priority})`;
                this.priorityOptions.push({ label, priority: rule.priority });

                const index = rule.priority - 1;
                const errorPath = ['variable_retrieval', 'input_datasets', 'rules', index, 'variable_mappings'].join('.');
                errorPaths[rule.priority] = errorPath;
            });
        }

        this.inputDatasetPath = ['variable_retrieval', 'input_datasets', props.inputDatasetName];

        // See if there are any errors on the mappings
        this.priorityErrors = {};
        if (props.processErrors) {
            Object.keys(props.processErrors).forEach(fieldPath => {
                Object.keys(errorPaths).forEach(priority => errorPath => {
                    if (fieldPath.startsWith(errorPath)) {
                        const errors = props.processErrors[fieldPath];
                        this.priorityErrors[priority] = errors;
                    }
                });

            });
        }
    }

    /**
     * Override from AbstractLocalStorageComponent so we don't have to pass the id via props
     */
    getLocalStorageKey() {
        return `${this.props.process.name}-input_dataset-${this.inputDatasetName}-variable_mapping`;
    }

    getErrorClassName(datastreamName) {
        const errors = this.priorityErrors[datastreamName];
        let className = '';
        if (errors) {
            const maxError = ProcessValidator.getRollupError(errors, this.props.process.name);
            className = ProcessValidator.getErrorClassName(maxError);
        }
        return className;
    }

    getVerticalAlignment(rowIndex) {
        let verticalAlignment = 'top';
        if (rowIndex === this.rowData.length - 1) {
            verticalAlignment = 'bottom'; // if this is the bottom row, then we want box to move up
        }
        return verticalAlignment;
    }

    getPriorityMappingsPath(variableName, priority) {
        const index = priority - 1;
        return this.inputDatasetPath.concat(['rules', index, 'variable_mappings', variableName]);
    }

    isRetrievedVariableNameValid = (name) => {
        const existingList = Object.keys(this.props.process.variable_retrieval.retrieved_variables);
        return this.validator.isRetrievedVariableNameValid(name, existingList);
    };

    onVariableNameChanged = (variableName, fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const oldName = variableName;
        const newName = value;
        editor.renameRetrievedVariable(oldName, newName, false);
        editor.commit();
    };

    onMappedVariablesChanged = (variableName, datastreamName, fieldPath, values) => {
        const retrievedVaribleDims = this.props.process.variable_retrieval.retrieved_variables[variableName].dims;
        const datastreamVariables = ProcessUtils.getDatastreamVariablesForDod(datastreamName);
        const coordSys = this.props.process.variable_retrieval.retrieved_variables[variableName].coordinate_system;
        const hasCoordSys = !!coordSys;

        let newValues = [];
        let dims = null;
        const omittedVariables = {};

        // If the value has been removed, then it will be returned as a single object, instead of an array
        if (Array.isArray(values)) {
            values.forEach(valueObj => {
                // we need to check if the selected values have different dims than the retrieved variable
                // retrieved variable
                const datastreamVariableName = valueObj.value;
                let datastreamVariableDims = null;
                if (datastreamVariables[datastreamVariableName]) {
                    datastreamVariableDims = datastreamVariables[datastreamVariableName].dims;
                }

                if (!hasCoordSys
                    && retrievedVaribleDims.length > 0
                    && datastreamVariableDims
                    && !_.isEqual(retrievedVaribleDims, datastreamVariableDims)) {
                    omittedVariables[datastreamVariableName] = datastreamVariableDims;

                } else {
                    if (!dims && datastreamVariableDims) {
                        dims = datastreamVariableDims;
                    }

                    if (newValues.indexOf(datastreamVariableName) == -1) {
                        newValues.push(datastreamVariableName);
                    }

                    const priorNames = datastreamVariables[datastreamVariableName] ? datastreamVariables[datastreamVariableName].renamed : null;
                    if (priorNames && priorNames.length > 0) {
                        priorNames.forEach(name => {
                            if (newValues.indexOf(name) == -1) {
                                newValues.push(name);
                            }
                        })
                    }
                }
            });
        }

        // Commit changes
        const editor = ProcessUtils.getEditor(this.props.process);
        const retrievedVariable = editor.process.variable_retrieval.retrieved_variables[variableName];

        // If the variable is new and has no dimensions assigned yet, then we get the dims from the assigned datastream
        // variable
        if (_.isEmpty(retrievedVariable.dims) && dims) {
            retrievedVariable.dims = dims;
        }
        editor.updateField(fieldPath, newValues);
        editor.commit();

        if (Object.keys(omittedVariables).length > 0) {
            const list = Object.keys(omittedVariables).map((vName) => {
                const variableDims = omittedVariables[vName];
                return <li key={vName}>{vName} ({variableDims.join(', ')})</li>;
            });

            const message = (
                <div>
                    <div style={{ marginBottom: '10px' }}>
                        The following datastream variables could not be mapped because they do not have the same shape as the assigned variable
                        <span className="entity-name">{variableName} ({retrievedVaribleDims.join(', ')})</span>:
                    </div>
                    <ul>{list}</ul>
                </div>);

            // show a message telling the user why their variables were skipped
            ModalsService.openModal({
                id: 'mappings_error',
                modalType: ModalsService.TYPE_ERROR,
                modalTitle: 'Can\'t Assign Variables',
                modalMessage: message
            });

        }
    };

    onAddVariableClick = () => {
        const modalComponent = (
            <NewRetrievalRuleVariable
                process={this.props.process}
                inputDatasetName={this.props.inputDatasetName}
            />
        );

        ModalsService.openModal({
            id: ModalsService.TYPE_DATASET_VARIABLE,
            modalType: ModalsService.TYPE_CUSTOM,
            modalComponent
        });
    };

    onDeleteVariablesClick = () => {
        // Delete the variables represented by each row
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.retrieved_variables',
            this.state.selectedItems, this.validator.canRetrievedVariablesBeDeleted);
        this.clearSelection();
    };

    renderDatastreamHeader(datastreamName, priority) {
        const flexDirection = this.props.displayOnlyVariable ? 'row' : 'column';
        return (
            <Flexbox flexDirection={flexDirection} alignItems="center">
                <span className="datastream-name">{datastreamName}</span>
                <span className="datastream-priority">(priority {priority})</span>
            </Flexbox>
        );
    }

    renderVariableName(variableName, extras) {
        const onNameChanged = this.onVariableNameChanged.bind(this, variableName);
        const verticalAlignment = this.getVerticalAlignment(extras.rowIndex);

        const highlightClass = (this.props.highlightVariableName && this.props.highlightVariableName === variableName) ? 'extrabold' : '';
        return (
            <div className={highlightClass}>
                <RIEInput
                    value={variableName}
                    change={onNameChanged}
                    fieldPath={['variable_retrieval', 'retrieved_variables', variableName, 'name']}
                    processId={this.props.process.name}
                    processErrors={this.props.processErrors}
                    isDisabled={!this.props.hasWritePermissions}
                    verticalAlignment={verticalAlignment}
                    minWidth="200px"
                    urlCommit
                    checkUnique={{ list: Object.keys(this.props.process.variable_retrieval.retrieved_variables), label: 'retrieved variable' }}
                />
            </div>
        );
    }

    getDatastreamVariableLabel(datastreamVariableName, dims) {
        //return `${datastreamVariableName} (${dims.join(',')})`; // this may be more confusing than it's worth
        return datastreamVariableName;
    }

    renderDatastreamVariableNames(variableName, priority, extras) {

        const rules = this.inputDataset.rules;
        const datastreamName = rules[priority - 1].datastream_name;

        const fieldPath = this.getPriorityMappingsPath(variableName, priority);
        let values = [];
        const dims = this.props.process.variable_retrieval.retrieved_variables[variableName].dims;

        const datastreamVariables = _.get(this.props.process, fieldPath);
        if (datastreamVariables) {
            values = datastreamVariables.map(datastreamVariableName => {
                const label = this.getDatastreamVariableLabel(datastreamVariableName, dims);
                return { value: datastreamVariableName, label };
            });
        }

        const variables = ProcessUtils.getDatastreamVariablesForDod(datastreamName);
        const datastreamVariableOptions = [];
        if (variables) {
            Object.keys(variables).forEach(datastreamVariableName => {
                const variable = variables[datastreamVariableName];
                const label = this.getDatastreamVariableLabel(datastreamVariableName, variable.dims);
                datastreamVariableOptions.push({ value: datastreamVariableName, label });
            });

        } else {
            datastreamVariableOptions.push({ value: 'Loading...', label: 'Loading...' });
        }

        // determine vertical alignment for rie box
        const verticalAlignment = this.getVerticalAlignment(extras.rowIndex);

        const onMappedVariablesChanged = this.onMappedVariablesChanged.bind(this, variableName, datastreamName);

        return (
            <RIEComboBox
                value={values}
                options={Constants.sortSelectOptions(datastreamVariableOptions)}
                change={onMappedVariablesChanged}
                fieldPath={fieldPath}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                horizontalAlignment="right"
                verticalAlignment={verticalAlignment}
                minWidth="200px"
                multi
                clearable
                creatable
            />
        );
    }

    renderToolbar() {
        if (this.props.hideToolbar) {
            return <div className="empty-toolbar" />;
        } else {
            return (
                <Toolbar selectedItems={this.getSelectedItems()}>
                    <ToolbarItem
                        title="Delete"
                        className="fa fa-times delete"
                        clickHandler={this.onDeleteVariablesClick}
                        selectionDependent
                        enabled={this.props.hasWritePermissions}
                    />
                    <ToolbarItem
                        title="New"
                        className="fa fa-plus new"
                        clickHandler={this.onAddVariableClick}
                        enabled={this.props.hasWritePermissions}
                    />
                </Toolbar>
            );
        }
    }

    getRowKey() {
        return 'variableName';
    }

    /**
     * Must be defined as an array of objects (not a map)
     */
    getTableRowData(props) {
        const data = [];
        const variableNames = ProcessUtils.getRetrievedVariablesForDataset(props.process, props.inputDatasetName);

        variableNames.forEach(variableName => {
            if (!this.props.displayOnlyVariable || this.props.displayOnlyVariable === variableName) {
                const variableData = {
                    variableName
                };
                data.push(variableData);
            }
        });

        return data;
    }

    getTableColumns() {
        const rules = this.inputDataset.rules;
        const columns = [];

        if (!this.props.displayOnlyVariable) {
            columns.push(
                {
                    property: 'variableName',
                    header: {
                        label: 'Assigned Name'
                    },
                    cell: {
                        formatters: [
                            (variableName, extras) => {
                                return this.renderVariableName(variableName, extras);
                            }
                        ]
                    }
                }
            );
        }

        // Add the datastream columns
        rules.forEach((rule, index) => {
            const datastreamName = rule.datastream_name;
            const priority = index + 1;
            columns.push(
                {
                    property: 'variableName',
                    header: {
                        label: datastreamName,
                        formatters: [
                            variableName => { return this.renderDatastreamHeader(datastreamName, priority); }
                        ]
                    },
                    cell: {
                        formatters: [
                            (variableName, extras) => { return this.renderDatastreamVariableNames(variableName, priority, extras); }
                        ]
                    }
                }
            );
        });

        return columns;
    }

}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/inputs/MappingsTable.js